'use client'

import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  InlineDialog,
  InlineDialogHeader,
  InlineDialogBody,
  InlineDialogFooter,
  InlineDialogTitle,
  InlineDialogDescription,
} from './inline-dialog'
import { Coffee, Play } from 'lucide-react'
import { useTranslations } from 'next-intl'
import type { DemoBreakStatus } from '../demo-types'

export interface BreakControlsProps {
  breakStatus: DemoBreakStatus
  isDialogOpen: boolean
  onSetDialogOpen: (open: boolean) => void
  onStartBreak: (durationMinutes: number) => void
  onEndBreak: () => void
}

export function BreakControls({
  breakStatus,
  isDialogOpen,
  onSetDialogOpen,
  onStartBreak,
  onEndBreak,
}: BreakControlsProps) {
  const t = useTranslations('demo')
  const [breakDuration, setBreakDuration] = useState('15')

  const handleStartBreak = () => {
    onStartBreak(parseInt(breakDuration))
    onSetDialogOpen(false)
  }

  return (
    <>
      {/* Break Banner */}
      {breakStatus.onBreak && (
        <Card className="border-amber-500/50 bg-gradient-to-r from-amber-950/50 to-orange-950/50">
          <CardContent className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-amber-500/20 shrink-0">
                <Coffee className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <p className="font-semibold text-white">{t('onBreak')}</p>
                <p className="text-sm text-zinc-400">
                  {t('expectedBackAt', { time: breakStatus.expectedReturnAt
                    ? new Date(breakStatus.expectedReturnAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                    : t('expectedBackSoon') })}
                </p>
              </div>
            </div>
            <Button onClick={onEndBreak} size="sm" className="bg-gradient-to-r from-green-600 to-emerald-600">
              <Play className="mr-2 h-4 w-4" />
              {t('imBack')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Break Dialog */}
      <InlineDialog open={isDialogOpen} onOpenChange={onSetDialogOpen} className="max-w-sm">
        <InlineDialogHeader onClose={() => onSetDialogOpen(false)}>
          <InlineDialogTitle className="flex items-center gap-2">
            <Coffee className="h-5 w-5 text-amber-400" />
            {t('takeABreak')}
          </InlineDialogTitle>
          <InlineDialogDescription>
            {t('breakDescription')}
          </InlineDialogDescription>
        </InlineDialogHeader>
        <InlineDialogBody>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-zinc-300 mb-3">{t('howLongBreak')}</p>
              <div className="grid grid-cols-4 gap-2">
                {['5', '10', '15', '20', '30', '45', '60'].map((mins) => (
                  <Button
                    key={mins}
                    variant={breakDuration === mins ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setBreakDuration(mins)}
                    className={breakDuration === mins ? 'bg-amber-600 hover:bg-amber-700' : 'border-zinc-700 hover:bg-zinc-800'}
                  >
                    {mins}m
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </InlineDialogBody>
        <InlineDialogFooter>
          <Button variant="outline" onClick={() => onSetDialogOpen(false)} className="border-zinc-700">{t('cancel')}</Button>
          <Button onClick={handleStartBreak} className="bg-gradient-to-r from-amber-600 to-orange-600">
            <Coffee className="mr-2 h-4 w-4" />{t('startBreak')}
          </Button>
        </InlineDialogFooter>
      </InlineDialog>
    </>
  )
}
